import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  TruckIcon,
  MapPinIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  CalendarDaysIcon,
  PhoneIcon,
  SignalIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { getRoutes, type TransportRoute, type GetRoutesParams } from '@/api/routesApi'
import { getTrips, type Trip, type GetTripsParams } from '@/api/tripsApi'
import StartTripModal from '@/components/transport/StartTripModal'
import ActiveTripView from '@/components/transport/ActiveTripView'
import toast from 'react-hot-toast'

interface DriverStats {
  totalRoutes: number
  totalStudents: number
  todayTrips: number
  completedTrips: number
  upcomingTrips: number
  activeTrip: Trip | null
}

const DriverDashboard: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<DriverStats>({
    totalRoutes: 0,
    totalStudents: 0,
    todayTrips: 0,
    completedTrips: 0,
    upcomingTrips: 0,
    activeTrip: null,
  })
  const [assignedRoutes, setAssignedRoutes] = useState<TransportRoute[]>([])
  const [todayTrips, setTodayTrips] = useState<Trip[]>([])

  // Modal states
  const [showStartTripModal, setShowStartTripModal] = useState(false)
  const [showActiveTripView, setShowActiveTripView] = useState(false)
  const [selectedTripForStart, setSelectedTripForStart] = useState<string | undefined>()
  const [currentActiveTrip, setCurrentActiveTrip] = useState<Trip | null>(null)

  useEffect(() => {
    fetchDriverData()
  }, [])

  const fetchDriverData = async () => {
    setLoading(true)
    try {
      // Fetch routes assigned to this driver
      const routesResponse = await getRoutes({ assignedDriverId: user?.id } as GetRoutesParams)
      const routes = routesResponse.data?.routes || []
      setAssignedRoutes(routes)

      // Calculate total students across all routes
      const totalStudents = routes.reduce((sum: number, route: TransportRoute) => {
        return sum + (route.stops?.length || 0)
      }, 0)

      // Fetch today's trips for this driver
      const tripsResponse = await getTrips({
        driverId: user?.id,
        status: 'active', // Get active trips (enabled trips).  dfdf
      } as GetTripsParams)
      const trips = tripsResponse.data?.trips || []
      setTodayTrips(trips)

      // Find active trip (one with an active session - indicated by operatingToday)
      const activeTrip = trips.find((trip: Trip) =>
        trip.status === 'active' && trip.operatingToday
      )

      // Calculate stats - in new model, trips are templates, sessions track status
      const completedTrips = 0 // Would need to check TripSessions
      const upcomingTrips = trips.filter((trip: Trip) =>
        trip.status === 'active'
      ).length

      setStats({
        totalRoutes: routes.length,
        totalStudents,
        todayTrips: trips.length,
        completedTrips,
        upcomingTrips,
        activeTrip: activeTrip || null,
      })

    } catch (error) {
      console.error('Failed to fetch driver data:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const formatTime = (timeString: string) => {
    if (!timeString) return 'N/A'
    try {
      return new Date(timeString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return timeString
    }
  }

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'in_progress':
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'completed':
        return 'bg-blue-100 text-blue-800'
      case 'scheduled':
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleStartTrip = () => {
    setSelectedTripForStart(undefined)
    setShowStartTripModal(true)
  }

  const handleTripStarted = (trip: Trip) => {
    setCurrentActiveTrip(trip)
    setShowStartTripModal(false)
    setShowActiveTripView(true)
    // Update stats
    setStats(prev => ({ ...prev, activeTrip: trip }))
  }

  const handleViewActiveTrip = () => {
    if (stats.activeTrip) {
      setCurrentActiveTrip(stats.activeTrip)
      setShowActiveTripView(true)
    }
  }

  const handleTripEnded = () => {
    setShowActiveTripView(false)
    setCurrentActiveTrip(null)
    fetchDriverData() // Refresh data
  }

  const handleStartScheduledTrip = (tripId: string) => {
    setSelectedTripForStart(tripId)
    setShowStartTripModal(true)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <TruckIcon className="h-8 w-8 text-blue-600 mr-3" />
            Driver Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            Welcome back, {user?.firstName}! Here's your schedule for today.
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Today's Date</p>
          <p className="text-lg font-semibold text-gray-900">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
      </div>

      {/* Active Trip Alert */}
      {stats.activeTrip && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <PlayIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-green-900">Active Trip in Progress</h3>
                <p className="text-sm text-green-700">
                  {stats.activeTrip.tripName || stats.activeTrip.route?.routeName || 'Current Route'} - {stats.activeTrip.tripType || 'Transport'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm text-green-600">Students on board</p>
                <p className="text-2xl font-bold text-green-800">
                  {stats.activeTrip.studentCount || 0}
                </p>
              </div>
              <button
                onClick={handleViewActiveTrip}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                View Trip
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-stat-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Assigned Routes</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalRoutes}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MapPinIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="dashboard-stat-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Students</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalStudents}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <UserGroupIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="dashboard-stat-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Today's Trips</p>
              <p className="text-3xl font-bold text-gray-900">{stats.todayTrips}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <CalendarDaysIcon className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="dashboard-stat-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed Today</p>
              <p className="text-3xl font-bold text-gray-900">{stats.completedTrips}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Schedule */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <ClockIcon className="h-5 w-5 text-blue-600 mr-2" />
              Today's Schedule
            </h2>
            <span className="text-sm text-gray-500">{todayTrips.length} trips</span>
          </div>

          {todayTrips.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDaysIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No trips scheduled for today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {todayTrips.map((trip, index) => (
                <div
                  key={trip.id || index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      trip.tripType === 'pickup' ? 'bg-blue-100' : 'bg-orange-100'
                    }`}>
                      <TruckIcon className={`h-5 w-5 ${
                        trip.tripType === 'pickup' ? 'text-blue-600' : 'text-orange-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {trip.tripName || trip.route?.routeName || 'Route'} - {trip.tripType === 'pickup' ? 'Morning Pickup' : 'Afternoon Dropoff'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {trip.startTime || '--:--'} - {trip.currentEnrollment || trip.enrolledStudents?.length || 0} students
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(trip.status)}`}>
                      {trip.status || 'Active'}
                    </span>
                    {trip.status === 'active' && (
                      <button
                        onClick={() => handleStartScheduledTrip(trip.id)}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <PlayIcon className="h-3 w-3 inline mr-1" />
                        Start
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Assigned Routes */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <MapPinIcon className="h-5 w-5 text-green-600 mr-2" />
              My Routes
            </h2>
            <span className="text-sm text-gray-500">{assignedRoutes.length} routes</span>
          </div>

          {assignedRoutes.length === 0 ? (
            <div className="text-center py-8">
              <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No routes assigned yet</p>
              <p className="text-sm text-gray-400 mt-1">Contact your administrator</p>
            </div>
          ) : (
            <div className="space-y-3">
              {assignedRoutes.map((route, index) => (
                <div
                  key={route.id || index}
                  className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{route.routeName || route.routeNumber}</h3>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      route.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {route.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <MapPinIcon className="h-4 w-4 mr-1" />
                      {route.stops?.length || 0} stops
                    </span>
                    <span className="flex items-center">
                      <UserGroupIcon className="h-4 w-4 mr-1" />
                      {route.stopCount || route.stops?.length || 0} students
                    </span>
                    <span className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {route.operatingDaysCount || route.operatingDays?.length || 0} days/week
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={handleStartTrip}
            className="flex flex-col items-center justify-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <PlayIcon className="h-8 w-8 text-blue-600 mb-2" />
            <span className="text-sm font-medium text-blue-900">Start Trip</span>
          </button>

          <button
            onClick={stats.activeTrip ? handleViewActiveTrip : handleStartTrip}
            className="flex flex-col items-center justify-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
          >
            <CheckCircleIcon className="h-8 w-8 text-green-600 mb-2" />
            <span className="text-sm font-medium text-green-900">
              {stats.activeTrip ? 'View Attendance' : 'Mark Attendance'}
            </span>
          </button>

          <button className="flex flex-col items-center justify-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
            <ExclamationTriangleIcon className="h-8 w-8 text-orange-600 mb-2" />
            <span className="text-sm font-medium text-orange-900">Report Issue</span>
          </button>

          <button className="flex flex-col items-center justify-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
            <PhoneIcon className="h-8 w-8 text-purple-600 mb-2" />
            <span className="text-sm font-medium text-purple-900">Emergency</span>
          </button>
        </div>
      </motion.div>

      {/* Vehicle Status (if assigned) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <TruckIcon className="h-5 w-5 text-blue-600 mr-2" />
            My Vehicle
          </h2>
          <span className="flex items-center text-green-600">
            <SignalIcon className="h-4 w-4 mr-1" />
            GPS Active
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Vehicle Number</p>
            <p className="font-semibold text-gray-900">{(user as any)?.vehicleAssigned || 'Not Assigned'}</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">License Plate</p>
            <p className="font-semibold text-gray-900">{(user as any)?.licenseNumber || 'N/A'}</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Capacity</p>
            <p className="font-semibold text-gray-900">45 seats</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Status</p>
            <p className="font-semibold text-green-600">Operational</p>
          </div>
        </div>
      </motion.div>

      {/* Start Trip Modal */}
      <StartTripModal
        isOpen={showStartTripModal}
        onClose={() => setShowStartTripModal(false)}
        onTripStarted={handleTripStarted}
        preselectedTripId={selectedTripForStart}
      />

      {/* Active Trip View */}
      {showActiveTripView && currentActiveTrip && (
        <ActiveTripView
          trip={currentActiveTrip}
          onTripEnded={handleTripEnded}
          onClose={() => setShowActiveTripView(false)}
        />
      )}
    </div>
  )
}

export default DriverDashboard
